import React, { useState, useEffect } from 'react';
import { pick } from 'lodash';
import { CircularProgress, Grid, Box, TextField as MuiTextField, FormControl, Select as MuiSelect, Divider } from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useHistory, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import useUser from '../lib/useUser';
import { apiFetch } from '../lib/fetch';
import Layout from '../components/layout';
import { Button } from '../lib/common';
import { NumericFormat } from 'react-number-format';
import { formatCurrency } from '../lib/util';
import { colors } from '../lib/styles';
import IncomePlanWrapper from '../components/income-plan-wrapper';
import { toggleForm, generateRedesignedIncomePlan } from '../lib/client';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  ChartDataLabels,
);

// Custom NumericFormatCustom component for currency input
const NumericFormatCustom = React.forwardRef(function NumericFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      valueIsNumericString
      prefix="$"
      decimalScale={2}
      fixedDecimalScale
    />
  );
});

// Custom NumericFormatInteger component for age inputs
const NumericFormatInteger = React.forwardRef(function NumericFormatInteger(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      valueIsNumericString
      decimalScale={0}
      allowNegative={false}
      isAllowed={(values) => {
        const { value } = values;
        return (value >= 0 && value <= 120) || value === '';
      }}
    />
  );
});

const styles = {
  mainBody: {
    padding: '20px',
  },
  errTxt: {
    color: 'red',
    margin: '5px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '1.5em',
  },
}

const ClientRetirementPlan = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const history = useHistory();
  const auth = useUser();
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [err, setErr] = useState(null);
  const [savingView, setSavingView] = useState(false);
  
  useEffect(() => {
    if (!auth?.id) return;
    apiFetch(`/users/${auth.id}/ip_households/${id}`)
    .then((res) => {
      return generateRedesignedIncomePlan(res);
    }).then((generatedClient) => {
      setClient(generatedClient);
    }).catch((e) => {
      let err = e.toString();
      setErr(err);
    });
  }, [auth?.id, id]);

  if (!client) {
    return <Layout><div>Loading...</div></Layout>;
  }

  const {
    leg1Compounded,
    leg2Compounded,
    leg3Compounded,
    displayName,
    totalRetAssets,
    age,
    spouseAge,
    emergencyFunds,
    planIncomePrincipal,
    growthPrincipal,
    leg1AnnualIncome,
    leg2AnnualIncome,
    leg3AnnualIncome,
    recoveryAssets,
    additionalNeedsPercentage,
    pieChartIncomePercentage,
    pieChartGrowthPercentage,
  } = client;

  // Plan Names is a display toggle with no dependent validation, so persist it
  // immediately on change (rather than waiting for the Save button) — otherwise
  // it silently reverts when the user navigates away without saving.
  const handlePlanNamesChange = async (evt) => {
    const planNames = evt.target.value === 'true' ? true : evt.target.value === 'false' ? false : evt.target.value;
    const newClient = { ...client, plan_names: planNames };
    const redesignedClient = await generateRedesignedIncomePlan(newClient);
    setClient(redesignedClient);
    try {
      await apiFetch.put(`/users/${auth.id}/ip_households/${id}`, { plan_names: planNames });
    } catch (e) {
      setErr(e.toString());
    }
  };

  const handleTextChange = async (evt) => {
    const { name, value } = evt.target;
    const parsedValue = value === 'true' ? true : value === 'false' ? false : value;
    const newClient = { ...client, [name]: parsedValue };
    const redesignedClient = await generateRedesignedIncomePlan(newClient);
    setClient(redesignedClient);
  };

  const handleMaskEditChange = async (evt) => {
    // Handle both legacy MaskInput format and new direct TextField format
    const { name, value } = evt.target ? evt.target : evt;
    const newClient = { ...client, [name]: value };
    const redesignedClient = await generateRedesignedIncomePlan(newClient);
    setClient(redesignedClient);
  }

  const handleSubmit = async () => {
    setErr(null);
    // const leg1Allocation = Number(client.leg1_allocation) || 65;
    // const leg2Allocation = Number(client.leg2_allocation) || 20;
    // const leg3Allocation = Number(client.leg3_allocation) || 15;
    const totalPrincipals = Number(client.leg1_principle) + Number(client.leg2_principle) + Number(client.leg3_principle);
    if (totalPrincipals > Number(planIncomePrincipal)) {
      setErr('Principal allocations must not total greater than income bucket');
      return;
    }
    const toSave = pick(client, [
      'firstname',
      'lastname',
      'dob',
      'leg1_compounded',
      'leg2_compounded',
      'leg3_compounded',
      'inflation_rate',
      'desired_recovery',
      'emergency_fund',
      'effective_tax_rate',
      'leg1_allocation',
      'leg2_allocation',
      'leg3_allocation',
      'leg1_income_bonus_rate',
      'leg2_income_bonus_rate',
      'leg3_income_bonus_rate',
      'leg1_rider_annual_increase',
      'leg2_rider_annual_increase',
      'leg3_rider_annual_increase',
      'leg1_withdrawal_rate',
      'leg2_withdrawal_rate',
      'leg3_withdrawal_rate',
      'leg1_age_to_begin',
      'leg2_age_to_begin',
      'leg3_age_to_begin',
      'inflation_rate',
      'plan_year',
      'income_bucket',
      'plan_names',
      'leg1_product',
      'leg2_product',
      'leg3_product',
      'leg1_years_deferred',
      'leg2_years_deferred',
      'leg3_years_deferred',
      'leg1_principle',
      'leg2_principle',
      'leg3_principle',
      'years_to_grow',
      'assumed_net_growth',
      'leg1_rider_rate',
      'leg2_rider_rate',
      'leg3_rider_rate',
    ]);
    toSave.desired_recovery = toSave.desired_recovery || totalRetAssets;
    toSave.effective_tax_rate = Number(toSave.effective_tax_rate) || 0.0;
    toSave.emergency_fund = toSave.emergency_fund || 100000;
    console.log('saving', toSave, auth.id, id, 'ASSETS', client.assets);
    try {
      const saved = await apiFetch.put(`/users/${auth.id}/ip_households/${id}`, toSave);
      saved.assets = client.assets;
      setClient(saved);
      setOpen(true);
      setMessage('Saved');
      return saved;
    } catch (e) {
      let err = e.toString();
      setErr(err);
    }
    return null;
  };
  
  const pieData = {
    labels: [
          'ECP',
          'Income',
          'Growth'
        ],
    datasets: [
      {
        data: [
          additionalNeedsPercentage,
          pieChartIncomePercentage,
          pieChartGrowthPercentage
        ],
        backgroundColor: [
          colors.primaryLighter,
          colors.primaryColor,
          colors.primaryDarker,
        ],
        borderColor: [
          colors.primaryLighter,
          colors.primaryColor,
          colors.primaryDarker,
        ],
      },
    ],
  };

  const options = {
    plugins: {
      datalabels: {
        formatter: (value) => value + '%',
        color: '#fff',
        font: {
          weight: 'bold',
          size: 24
        }
      },
      tooltip: {
        enabled: false,
      },
      legend: {
        position: 'top',
        labels: {
          boxWidth: 20,
          padding: 20,
          font: {
            size: 24,
            weight: 'bold',
          },
        },
      },
    }
  };

  return (
    <IncomePlanWrapper handleSubmit={handleSubmit}>
      <Box style={styles.mainBody}>
        <div style={styles.errTxt}>{err?.toString()}</div>
        {client ? (
          <div style={{ margin: '10px' }}>
            {client.income_plan_forms ? (
              <>
                <Button
                  endIcon={<RemoveIcon/>}
                  variant='link'
                  disabled={savingView}
                  sx={{
                    color: colors.white,
                    backgroundColor: colors.primaryColor,
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    '&:hover': {
                      color: colors.white,
                      backgroundColor: colors.primaryAccent,
                    }
                  }}
                  onClick={async () => {
                    setSavingView(true);
                    const updated = await toggleForm(auth, client, 'income_plan_forms', false);
                    setSavingView(false);
                    const redesignedClient = await generateRedesignedIncomePlan(updated);
                    setClient(redesignedClient);
                  }}
                >
                  Hide Forms
                </Button>
                <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ 
                      backgroundColor: colors.white,
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Box sx={{ 
                        backgroundColor: '#4F5F4B', 
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        color: colors.white,
                        textAlign: 'center', 
                        p: 2,
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', md: '1.3rem' },
                        padding: '5px',
                      }}>
                        Client Assumptions
                      </Box>
                      <Box sx={{ p: 2, flexGrow: 1, borderTop: 'none', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
                        <Grid container spacing={1} id="client-edit-form">
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 2 }}>Client Age:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography sx={{ mb: 2 }}>{age}</Typography>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 2 }}>Spouse Age:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography sx={{ mb: 2 }}>{spouseAge}</Typography>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 2 }}>Starting Total Assets:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography sx={{ mb: 2 }}>${formatCurrency(totalRetAssets)}</Typography>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Plan Year:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <MuiTextField
                              type="number"
                              size="small"
                              fullWidth
                              value={client.plan_year || ''}
                              onChange={(e) => {
                                const year = parseInt(e.target.value, 10);
                                setClient({ ...client, plan_year: year});
                              }}
                              InputProps={{
                                inputComponent: NumericFormatInteger,
                                style: { color: colors.black }
                              }}
                              sx={{ mb: 2 }}
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Emergency Bucket:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <MuiTextField
                              size="small"
                              fullWidth
                              value={client.emergency_fund || ''}
                              name="emergency_fund"
                              onChange={handleMaskEditChange}
                              InputProps={{
                                inputComponent: NumericFormatCustom,
                                style: { color: colors.black }
                              }}
                              sx={{ mb: 2 }}
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Income Bucket:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <MuiTextField
                              size="small"
                              fullWidth
                              value={client.income_bucket || ''}
                              name="income_bucket"
                              onChange={handleMaskEditChange}
                              InputProps={{
                                inputComponent: NumericFormatCustom,
                                style: { color: colors.black }
                              }}
                              sx={{ mb: 2 }}
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Plan Names:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                              <MuiSelect
                                value={client.plan_names === true ? true : client.plan_names === false ? false : ''}
                                name="plan_names"
                                onChange={handlePlanNamesChange}
                                sx={{ '& .MuiSelect-select': { color: colors.black } }}
                              >
                                <MenuItem value={true}>Yes</MenuItem>
                                <MenuItem value={false}>No</MenuItem>
                              </MuiSelect>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ 
                      backgroundColor: colors.white, 
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      overflow: 'hidden',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Box sx={{ 
                        backgroundColor: '#4F5F4B', 
                        color: 'white',
                        textAlign: 'center', 
                        p: 2,
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', md: '1.3rem' },
                        padding: '5px',
                      }}>
                        Income
                      </Box>
                      <Box sx={{ p: 2, flexGrow: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 2 }}>
                              Total Income Principal:
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography sx={{ mb: 2, fontWeight: 'bold' }}>
                              ${formatCurrency(planIncomePrincipal)}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Box sx={{ height: '100%' }}></Box>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <Box sx={{ 
                                  backgroundColor: '#4F5F4B',
                                  color: 'white',
                                  p: 1,
                                  borderRadius: '4px',
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  mb: 2
                                }}>
                                  Leg 1
                                </Box>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <Box sx={{ 
                                  backgroundColor: '#4F5F4B',
                                  color: 'white',
                                  p: 1,
                                  borderRadius: '4px',
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  mb: 2
                                }}>
                                  Leg 2
                                </Box>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <Box sx={{ 
                                  backgroundColor: '#4F5F4B',
                                  color: 'white',
                                  p: 1,
                                  borderRadius: '4px',
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  mb: 2
                                }}>
                                  Leg 3
                                </Box>
                              </Grid>
                            </Grid>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Typography sx={{ color: colors.black, fontWeight: 'bold', mt: 1 }}>
                                  Starting Principal:
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  size="small"
                                  fullWidth
                                  value={client.leg1_principle || ''}
                                  name="leg1_principle"
                                  onChange={handleMaskEditChange}
                                  InputProps={{
                                    inputComponent: NumericFormatCustom,
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  size="small"
                                  fullWidth
                                  value={client.leg2_principle || ''}
                                  name="leg2_principle"
                                  onChange={handleMaskEditChange}
                                  InputProps={{
                                    inputComponent: NumericFormatCustom,
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  size="small"
                                  fullWidth
                                  value={client.leg3_principle || ''}
                                  name="leg3_principle"
                                  onChange={handleMaskEditChange}
                                  InputProps={{
                                    inputComponent: NumericFormatCustom,
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Typography sx={{ color: colors.black, fontWeight: 'bold', mt: 1 }}>
                                  Product Name:
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  size="small"
                                  fullWidth
                                  value={client.leg1_product || ''}
                                  name="leg1_product"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  size="small"
                                  fullWidth
                                  value={client.leg2_product || ''}
                                  name="leg2_product"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  size="small"
                                  fullWidth
                                  value={client.leg3_product || ''}
                                  name="leg3_product"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Typography sx={{ color: colors.black, fontWeight: 'bold', mt: 1 }}>
                                  Years Deferred:
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg1_years_deferred || ''}
                                  name="leg1_years_deferred"
                                  onChange={handleTextChange}
                                  InputProps={{
                                    inputComponent: NumericFormatInteger,
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg2_years_deferred || ''}
                                  name="leg2_years_deferred"
                                  onChange={handleTextChange}
                                  InputProps={{
                                    inputComponent: NumericFormatInteger,
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg3_years_deferred || ''}
                                  name="leg3_years_deferred"
                                  onChange={handleTextChange}
                                  InputProps={{
                                    inputComponent: NumericFormatInteger,
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Typography sx={{ color: colors.black, fontWeight: 'bold', mt: 1 }}>
                                  Income Bonus Rate%:
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg1_income_bonus_rate || ''}
                                  name="leg1_income_bonus_rate"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg2_income_bonus_rate || ''}
                                  name="leg2_income_bonus_rate"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg3_income_bonus_rate || ''}
                                  name="leg3_income_bonus_rate"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Typography sx={{ color: colors.black, fontWeight: 'bold', mt: 1 }}>
                                  Rider Annual Increase%:
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg1_rider_annual_increase || ''}
                                  name="leg1_rider_annual_increase"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg2_rider_annual_increase || ''}
                                  name="leg2_rider_annual_increase"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg3_rider_annual_increase || ''}
                                  name="leg3_rider_annual_increase"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Typography sx={{ color: colors.black, fontWeight: 'bold', mt: 1 }}>
                                  Withdrawal Rate%:
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg1_withdrawal_rate || ''}
                                  name="leg1_withdrawal_rate"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg2_withdrawal_rate || ''}
                                  name="leg2_withdrawal_rate"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg3_withdrawal_rate || ''}
                                  name="leg3_withdrawal_rate"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Typography sx={{ color: colors.black, fontWeight: 'bold', mt: 1 }}>
                                  Rider Fee %:
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg1_rider_rate || ''}
                                  name="leg1_rider_rate"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg2_rider_rate || ''}
                                  name="leg2_rider_rate"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                              
                              <Grid item xs={3}>
                                <MuiTextField
                                  type="number"
                                  size="small"
                                  fullWidth
                                  value={client.leg3_rider_rate || ''}
                                  name="leg3_rider_rate"
                                  onChange={handleTextChange}
                                  inputProps={{
                                    style: { color: colors.black }
                                  }}
                                  sx={{ mb: 2 }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={3}>
                                <Typography sx={{ color: colors.black, fontWeight: 'bold', mt: 1 }}>
                                  Compounded / Simple:
                                </Typography>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                  <MuiSelect
                                    value={leg1Compounded}
                                    name="leg1_compounded"
                                    onChange={handleTextChange}
                                    sx={{ '& .MuiSelect-select': { color: colors.black } }}
                                  >
                                    <MenuItem value={true}>Compounded</MenuItem>
                                    <MenuItem value={false}>Simple</MenuItem>
                                  </MuiSelect>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                  <MuiSelect
                                    value={leg2Compounded}
                                    name="leg2_compounded"
                                    onChange={handleTextChange}
                                    sx={{ '& .MuiSelect-select': { color: colors.black } }}
                                  >
                                    <MenuItem value={true}>Compounded</MenuItem>
                                    <MenuItem value={false}>Simple</MenuItem>
                                  </MuiSelect>
                                </FormControl>
                              </Grid>
                              
                              <Grid item xs={3}>
                                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                  <MuiSelect
                                    value={leg3Compounded}
                                    name="leg3_compounded"
                                    onChange={handleTextChange}
                                    sx={{ '& .MuiSelect-select': { color: colors.black } }}
                                  >
                                    <MenuItem value={true}>Compounded</MenuItem>
                                    <MenuItem value={false}>Simple</MenuItem>
                                  </MuiSelect>
                                </FormControl>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ 
                      backgroundColor: colors.white, 
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      overflow: 'hidden',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      <Box sx={{ 
                        backgroundColor: '#4F5F4B', 
                        color: 'white',
                        textAlign: 'center', 
                        p: 2,
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', md: '1.3rem' },
                        padding: '5px',
                      }}>
                        Growth
                      </Box>
                      <Box sx={{ p: 2, flexGrow: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 2 }}>
                              Growth Principal:
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography sx={{ mb: 2, fontWeight: 'bold' }}>
                              ${formatCurrency(growthPrincipal || "0.00")}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>
                              Years to Grow:
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <MuiTextField
                              type="number"
                              size="small"
                              fullWidth
                              value={client.years_to_grow || ''}
                              name="years_to_grow"
                              onChange={handleTextChange}
                              InputProps={{
                                inputComponent: NumericFormatInteger,
                                style: { color: colors.black }
                              }}
                              sx={{ mb: 2 }}
                            />
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>
                              Assumed Net Growth %:
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <MuiTextField
                              type="number"
                              size="small"
                              fullWidth
                              value={client.assumed_net_growth || ''}
                              name="assumed_net_growth"
                              onChange={handleTextChange}
                              inputProps={{
                                style: { color: colors.black }
                              }}
                              sx={{ mb: 2 }}
                            />
                          </Grid>

                          <Grid item xs={6}>
                            <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 2 }}>
                              Recovery Assets:
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography sx={{ mb: 2, fontWeight: 'bold' }}>
                              ${formatCurrency(recoveryAssets || "0.00")}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </>
            ) : (
              <Button
                endIcon={<AddIcon/>}
                variant='link'
                disabled={savingView}
                sx={{
                  mb: 2,
                  color: colors.white,
                  backgroundColor: colors.primaryColor,
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  '&:hover': {
                    color: colors.white,
                    backgroundColor: colors.primaryAccent,
                  }
                }}
                onClick={async () => {
                  setSavingView(true);
                  const updated = await toggleForm(auth, client, 'income_plan_forms', true);
                  setSavingView(false);
                  const redesignedClient = await generateRedesignedIncomePlan(updated);
                  setClient(redesignedClient);
                }}
              >
                Show Forms
              </Button>
            )}
            <Box elevation={3}>
              <Box sx={{ 
                backgroundColor: colors.white,
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Box sx={{ 
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  backgroundColor: '#4F5F4B', 
                  color: 'white',
                  textAlign: 'center', 
                  p: 2,
                  fontWeight: 'bold',
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                  padding: '5px',
                }}>
                  <Typography sx={{ fontWeight: 'bold', fontSize: 24, textAlign: 'center', width: '100%' }}>{displayName}</Typography>
                </Box>
                <Typography sx={{
                  fontWeight: 'bold',
                  fontSize: 24,
                  textAlign: 'center',
                  width: '100%',
                  fontStyle: 'italic',
                  p: 2,
                  padding: '5px',
                }}>
                  Starting Balance: ${formatCurrency(totalRetAssets)}
                </Typography>
              </Box>
              <Grid container spacing={1} sx={{ mt: 1, mb: 2, justifyContent: 'center' }}>
                {/* First Col */}
                <Grid item xs={12} lg={2} id="additional-needs-col">
                  <Box sx={{ 
                    p: 2,
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    backgroundColor: colors.white,
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 18 }}>Additional Needs</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 22, mt: 1 }}>${formatCurrency(emergencyFunds)}</Typography>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={12}>
                        <Box sx={{
                          p: 2,
                          textAlign: 'center',
                          alignContent: 'start',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          borderTopColor: colors.primaryLighter,
                          borderTopWidth: 5,
                          borderTopStyle: 'solid',
                          backgroundColor: colors.lightGrey,
                        }}>
                          <Typography sx={{ fontWeight: 'bold', fontSize: 18 }}>Emergency Funds Set Aside</Typography>
                          <Typography>${formatCurrency(emergencyFunds)}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                {/* Second Col */}
                <Grid item xs={12} lg={6} id="income-col">
                  <Box sx={{ 
                    p: 2,
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    backgroundColor: colors.white,
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 18 }}>Income</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 22, mt: 1 }}>${formatCurrency(planIncomePrincipal)}</Typography>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={4}>
                        <Box sx={{ 
                          p: 2,
                          textAlign: 'center',
                          alignContent: 'start',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          borderTopColor: colors.primaryColor,
                          borderTopWidth: 5,
                          borderTopStyle: 'solid',
                          backgroundColor: colors.lightGrey,
                        }}>
                          <Typography sx={{ fontWeight: 'bold', fontSize: 22 }}>
                            {client.plan_names ? client.leg1_product : 'Leg 1'}
                          </Typography>
                          <Divider sx={{ my: 1, borderColor: colors.grey }} />
                          <Box sx={{ p: 1, textAlign: 'center', mt: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Principal</Typography>
                            <Typography>${formatCurrency(Number(client.leg1_principle) || "0.00")}</Typography>
                          </Box>
                          <Box sx={{ p: 1, textAlign: 'center', mt: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Years Until Activating</Typography>
                            <Typography>{(Number(client.leg1_years_deferred) || 0)}</Typography>
                          </Box>
                          <Box sx={{ p: 1, textAlign: 'center', mt: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Income (Annual / Monthly)</Typography>
                            <Typography sx={{ fontWeight: 'bold' }}>${formatCurrency(leg1AnnualIncome || "0.00")}</Typography>
                            <Typography>${formatCurrency(leg1AnnualIncome / 12 || "0.00")}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ 
                          p: 2,
                          textAlign: 'center',
                          alignContent: 'start',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          borderTopColor: colors.primaryColor,
                          borderTopWidth: 5,
                          borderTopStyle: 'solid',
                          backgroundColor: colors.lightGrey,
                        }}>
                          <Typography sx={{ fontWeight: 'bold', fontSize: 22 }}>
                            {client.plan_names ? client.leg2_product : 'Leg 2'}
                          </Typography>
                          <Divider sx={{ my: 1, borderColor: colors.grey }} />
                          <Box sx={{ p: 1, textAlign: 'center', mt: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Principal</Typography>
                            <Typography>${formatCurrency(Number(client.leg2_principle) || "0.00")}</Typography>
                          </Box>
                          <Box sx={{ p: 1, textAlign: 'center', mt: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Years Until Activating</Typography>
                            <Typography>{(Number(client.leg2_years_deferred) || 0)}</Typography>
                          </Box>
                          <Box sx={{ p: 1, textAlign: 'center', mt: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Income (Annual / Monthly)</Typography>
                            <Typography sx={{ fontWeight: 'bold' }}>${formatCurrency(leg2AnnualIncome || "0.00")}</Typography>
                            <Typography>${formatCurrency(leg2AnnualIncome / 12 || "0.00")}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ 
                          p: 2,
                          textAlign: 'center',
                          alignContent: 'start',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          borderWidth: 2,
                          borderTopStyle: 'solid',
                          borderTopColor: colors.primaryColor,
                          borderTopWidth: 5,
                          backgroundColor: colors.lightGrey,
                        }}>
                          <Typography sx={{ fontWeight: 'bold', fontSize: 22 }}>
                            {client.plan_names ? client.leg3_product : 'Leg 3'}
                          </Typography>
                          <Divider sx={{ my: 1, borderColor: colors.grey }} />
                          <Box sx={{ p: 1, textAlign: 'center', mt: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Principal</Typography>
                            <Typography>${formatCurrency(Number(client.leg3_principle) || "0.00")}</Typography>
                          </Box>
                          <Box sx={{ p: 1, textAlign: 'center', mt: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Years Until Activating</Typography>
                            <Typography>{(Number(client.leg3_years_deferred) || 0)}</Typography>
                          </Box>
                          <Box sx={{ p: 1, textAlign: 'center', mt: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Income (Annual / Monthly)</Typography>
                            <Typography sx={{ fontWeight: 'bold' }}>${formatCurrency(leg3AnnualIncome || "0.00")}</Typography>
                            <Typography>${formatCurrency(leg3AnnualIncome / 12 || "0.00")}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                {/* Third Col */}
                <Grid item xs={12} lg={2} id="growth-col">
                  <Box sx={{ 
                    p: 2,
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    backgroundColor: colors.white,
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 18 }}>Growth</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 22, mt: 1 }}>${formatCurrency(growthPrincipal || "0.00")}</Typography>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={12}>
                        <Box sx={{
                          p: 2,
                          textAlign: 'center',
                          alignContent: 'start',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          borderTopColor: colors.primaryDarker,
                          borderTopWidth: 5,
                          borderTopStyle: 'solid',
                          backgroundColor: colors.lightGrey,
                        }}>
                          <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Starting Principal</Typography>
                          <Typography>${formatCurrency(growthPrincipal || "0.00")}</Typography>
                          <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Assumed Net Growth</Typography>
                            <Typography>{Number(client.assumed_net_growth || 0).toFixed(1)}%</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Years to Grow</Typography>
                            <Typography>{client.years_to_grow}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={1}
                sx={{ justifyContent: 'center'}}
              >
                <Grid item xs={12} lg={2} sx={{ pt: 0 }}>
                  <Box
                    sx={{
                      p: 1,
                      pt: 0,
                      textAlign: 'center',
                      backgroundColor: colors.white,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      borderRadius: '8px',
                      border: '5px solid #000',
                    }}>
                    <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Total Set Aside</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '24px' }}>${formatCurrency(emergencyFunds) || "0.00"}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} lg={2}>
                  <Box
                    sx={{
                    p: 1,
                    pt: 0,
                    textAlign: 'center',
                      backgroundColor: colors.white,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      borderRadius: '8px',
                      border: '5px solid #000',
                     }}>
                    <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Monthly Plan Income</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '24px' }}>
                      ${formatCurrency((leg1AnnualIncome) / 12 || "0.00")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} lg={2}>
                  <Box
                    sx={{
                    p: 1,
                    pt: 0,
                    textAlign: 'center',
                      backgroundColor: colors.white,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      borderRadius: '8px',
                      border: '5px solid #000',
                     }}>
                    <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Monthly Plan Income</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '24px' }}>
                      ${formatCurrency((leg1AnnualIncome + leg2AnnualIncome) / 12 || "0.00")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} lg={2}>
                  <Box
                    sx={{
                    p: 1,
                    pt: 0,
                    textAlign: 'center',
                      backgroundColor: colors.white,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      borderRadius: '8px',
                      border: '5px solid #000',
                     }}>
                    <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Monthly Plan Income</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '24px' }}>
                      ${formatCurrency((leg1AnnualIncome + leg2AnnualIncome + leg3AnnualIncome) / 12 || "0.00")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} lg={2}>
                  <Box
                    sx={{
                    p: 1,
                    pt: 0,
                    textAlign: 'center',
                      backgroundColor: colors.white,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      borderRadius: '8px',
                      border: '5px solid #000',
                     }}>
                    <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Projected Assets</Typography>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '24px' }}>${formatCurrency(recoveryAssets || "0.00")}</Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ 
                p: 1,
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                maxWidth: '500px',
                margin: '0 auto',
              }}>
                <Pie data={pieData} options={options} style={{ width: '100%'}} />
              </Box>
            </Box>
          </div>
        ) : <CircularProgress />}
      </Box>
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={() => setOpen(false)}
        message={message}
      />
    </IncomePlanWrapper>
  );
};


export default ClientRetirementPlan;
